<template>
    <box-view class="user-module" title="Quản lý Người dùng" :showBack="true" @close="$router.push('/admin')">
        <div class="container-fluid py-3">
            <!-- Header Actions -->
            <div class="row mb-4" v-if="isAdmin">
                <div class="col-12 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 class="mb-0 text-secondary">
                            <i class="fas fa-users mr-2"></i>
                            Tổng: <strong class="text-primary">{{ users.length }}</strong> người dùng
                        </h5>
                    </div>
                    <button class="btn btn-primary btn-add-user" @click="openModal()">
                        <i class="fas fa-plus mr-2"></i> Thêm người dùng
                    </button>
                </div>
            </div>

            <!-- User Cards Grid -->
            <div class="row">
                <div v-for="u in users" :key="u.id" class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="user-card card h-100 border-0 shadow-sm">
                        <div class="card-body p-4">
                            <div class="d-flex align-items-start mb-3">
                                <div class="user-avatar mr-3">
                                    <img :src="u.avatar || 'https://i.pravatar.cc/80?u=' + u.id" class="rounded-circle" width="60" height="60" alt="avatar" />
                                    <div class="status-indicator" :class="u.status === 'active' ? 'bg-success' : 'bg-secondary'"></div>
                                </div>
                                <div class="flex-grow-1">
                                    <h5 class="mb-1 font-weight-bold">{{ u.name }}</h5>
                                    <div class="text-muted small mb-2">
                                        <i class="fas fa-phone mr-1"></i> {{ u.phone }}
                                    </div>
                                    <span class="badge badge-pill" :class="getRoleBadge(u.role)">
                                        {{ getRoleLabel(u.role) }}
                                    </span>
                                </div>
                            </div>

                            <!-- User Info -->
                            <div class="user-info mb-3">
                                <div class="info-item">
                                    <span class="info-label">Trạng thái:</span>
                                    <span class="badge" :class="u.status === 'active' ? 'badge-success' : 'badge-secondary'">
                                        {{ u.status === 'active' ? 'Hoạt động' : 'Đã khóa' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">ID:</span>
                                    <span class="text-muted small">{{ u.id }}</span>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div v-if="isAdmin" class="user-actions d-flex gap-2">
                                <button class="btn btn-sm btn-outline-primary flex-fill" @click="openModal(u)">
                                    <i class="fas fa-edit mr-1"></i> Sửa
                                </button>
                                <button 
                                    class="btn btn-sm flex-fill" 
                                    :class="u.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'"
                                    @click="toggleStatus(u)">
                                    <i class="fas" :class="u.status === 'active' ? 'fa-lock' : 'fa-unlock'"></i>
                                    {{ u.status === 'active' ? 'Khóa' : 'Mở' }}
                                </button>
                                <button class="btn btn-sm btn-outline-danger" @click="handleDelete(u)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="users.length === 0" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-users fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Chưa có người dùng nào</h5>
                        <p class="text-muted">Hãy thêm người dùng đầu tiên để bắt đầu</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Thêm / Sửa -->
        <van-dialog v-if="isAdmin" v-model="showModal" :title="editing ? 'Sửa thông tin người dùng' : 'Thêm người dùng mới'" show-cancel-button @confirm="save">
            <div class="p-3">
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-phone mr-1"></i> Số điện thoại (Tài khoản ĐN)</label>
                    <input type="text" class="form-control" v-model="form.phone" :disabled="editing" placeholder="SĐT dùng đăng nhập">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-user mr-1"></i> Họ và tên</label>
                    <input type="text" class="form-control" v-model="form.name" placeholder="Họ tên đầy đủ">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-lock mr-1"></i> {{ editing ? 'Đổi mật khẩu (bỏ trống nếu không đổi)' : 'Mật khẩu' }}</label>
                    <input type="password" class="form-control" v-model="form.password" :placeholder="editing ? 'Nhập mật khẩu mới' : 'Mật khẩu'">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-user-tag mr-1"></i> Vai trò</label>
                    <select class="form-control" v-model="form.role">
                        <option value="staff">Nhân viên (Staff)</option>
                        <option value="accountant">Kế toán (Accountant)</option>
                        <option value="admin">Quản trị (Admin)</option>
                    </select>
                </div>
            </div>
        </van-dialog>
    </box-view>
</template>

<script>
export default {
    name: 'User',
    props: {
        users: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            default: () => ({ role: 'staff' })
        }
    },
    data() {
        return {
            showModal: false,
            editing: false,
            form: {
                id: null,
                username: '',
                phone: '',
                password: '',
                name: '',
                role: 'staff',
                status: 'active'
            }
        };
    },
    computed: {
        isAdmin() {
            return this.user && this.user.role === 'admin';
        }
    },
    methods: {
        getRoleBadge(role) {
            switch(role) {
                case 'admin': return 'badge-danger';
                case 'accountant': return 'badge-info';
                default: return 'badge-primary';
            }
        },
        getRoleLabel(role) {
            switch(role) {
                case 'admin': return 'Quản trị viên';
                case 'accountant': return 'Kế toán';
                case 'staff': return 'Nhân viên';
                default: return role;
            }
        },

        openModal(user = null) {
            if (user) {
                this.editing = true;
                // Clone object để tránh mutate trực tiếp prop
                this.form = { ...user, password: '' }; 
            } else {
                this.editing = false;
                this.form = {
                    id: 'USER-' + Date.now(),
                    username: '',
                    phone: '',
                    password: '',
                    name: '',
                    role: 'staff',
                    status: 'active'
                };
            }
            this.showModal = true;
        },

        save() {
            if (!this.form.phone || !this.form.name) {
                this.$toast?.('Vui lòng nhập Số điện thoại và Họ tên');
                return;
            }
            if (!this.editing && !this.form.password) {
                 this.$toast?.('Vui lòng nhập mật khẩu cho user mới');
                 return;
            }

            if (this.editing) {
                this.$emit('admin-update-user', this.form);
            } else {
                this.$emit('add-user', this.form);
            }
            this.showModal = false;
        },

        toggleStatus(user) {
            const newStatus = user.status === 'active' ? 'locked' : 'active';
            const action = newStatus === 'active' ? 'Mở khóa' : 'Khóa';
            if (confirm(`Bạn có chắc muốn ${action} tài khoản ${user.username}?`)) {
                this.$emit('toggle-user-status', { id: user.id, status: newStatus });
            }
        },
        handleDelete(user) {
            if (user.id === this.user.id) {
                this.$toast?.('Không thể tự xóa chính mình');
                return;
            }
            if (confirm(`Bạn có chắc muốn xóa tài khoản ${user.username}?`)) {
                this.$emit('delete-user', user.id);
            }
        }
    }
};
</script>
